/**
 * Sport Icons Utility
 * 
 * Provides sport-specific icons based on sport titles or keys
 */

import React from 'react';
import {
  SportsFootball,
  SportsBasketball,
  SportsBaseball,
  SportsHockey,
  SportsSoccer,
  SportsTennis,
  SportsGolf,
  SportsMma,
  SportsMotorsports,
  Sports as GenericSportsIcon
} from '@mui/icons-material';

// Map sport titles to icons
const SPORT_TITLE_TO_ICON = {
  // American Football
  'NFL': SportsFootball,
  'NCAAF': SportsFootball,
  'American Football': SportsFootball,
  
  // Basketball
  'NBA': SportsBasketball,
  'NCAAB': SportsBasketball,
  'WNBA': SportsBasketball,
  'Basketball': SportsBasketball,
  
  // Baseball
  'MLB': SportsBaseball,
  'Baseball': SportsBaseball,
  
  // Hockey
  'NHL': SportsHockey,
  'Hockey': SportsHockey,
  'Ice Hockey': SportsHockey,
  
  // Soccer
  'EPL': SportsSoccer,
  'MLS': SportsSoccer,
  'Soccer': SportsSoccer,
  'UEFA Champions League': SportsSoccer,
  'FIFA World Cup': SportsSoccer,
  'Premier League': SportsSoccer,
  
  // Tennis
  'ATP': SportsTennis,
  'WTA': SportsTennis,
  'Tennis': SportsTennis,
  
  // Golf
  'PGA': SportsGolf,
  'Golf': SportsGolf,
  'PGA Championship': SportsGolf,
  
  // MMA
  'MMA': SportsMma,
  'Mixed Martial Arts': SportsMma,
  'UFC': SportsMma,
  
  // Motorsports
  'NASCAR': SportsMotorsports,
  'Motorsport': SportsMotorsports,
  'Formula 1': SportsMotorsports,
  'F1': SportsMotorsports,
};

// Map sport keys to icons (for API sport_key format)
const SPORT_KEY_TO_ICON = {
  'americanfootball_nfl': SportsFootball,
  'americanfootball_ncaaf': SportsFootball,
  'basketball_nba': SportsBasketball,
  'basketball_ncaab': SportsBasketball,
  'basketball_wnba': SportsBasketball,
  'baseball_mlb': SportsBaseball,
  'icehockey_nhl': SportsHockey,
  'soccer_epl': SportsSoccer,
  'soccer_usa_mls': SportsSoccer,
  'soccer_uefa_champs_league': SportsSoccer,
  'soccer_fifa_world_cup': SportsSoccer,
  'tennis_wta': SportsTennis,
  'tennis_atp': SportsTennis,
  'golf_pga_championship': SportsGolf,
  'mma_mixed_martial_arts': SportsMma,
  'motorsport_nascar': SportsMotorsports,
};

/**
 * Get sport icon component by sport title
 * @param {string} sportTitle - Sport title (e.g., 'NFL', 'NBA', 'Soccer')
 * @returns {React.Component} Icon component
 */
export const getSportIconByTitle = (sportTitle) => {
  if (!sportTitle) return GenericSportsIcon;
  
  // Direct match
  let IconComponent = SPORT_TITLE_TO_ICON[sportTitle];
  if (IconComponent) return IconComponent;
  
  // Try case-insensitive match
  const normalizedTitle = sportTitle.toUpperCase();
  for (const [key, icon] of Object.entries(SPORT_TITLE_TO_ICON)) {
    if (key.toUpperCase() === normalizedTitle) {
      return icon;
    }
  }
  
  // Try partial matches for common patterns
  if (normalizedTitle.includes('FOOTBALL') || normalizedTitle.includes('NFL')) {
    return SportsFootball;
  }
  if (normalizedTitle.includes('BASKETBALL') || normalizedTitle.includes('NBA')) {
    return SportsBasketball;
  }
  if (normalizedTitle.includes('BASEBALL') || normalizedTitle.includes('MLB')) {
    return SportsBaseball;
  }
  if (normalizedTitle.includes('HOCKEY') || normalizedTitle.includes('NHL')) {
    return SportsHockey;
  }
  if (normalizedTitle.includes('SOCCER') || normalizedTitle.includes('FOOTBALL')) {
    return SportsSoccer;
  }
  if (normalizedTitle.includes('TENNIS')) {
    return SportsTennis;
  }
  if (normalizedTitle.includes('GOLF')) {
    return SportsGolf;
  }
  if (normalizedTitle.includes('MMA') || normalizedTitle.includes('MARTIAL')) {
    return SportsMma;
  }
  if (normalizedTitle.includes('NASCAR') || normalizedTitle.includes('MOTOR')) {
    return SportsMotorsports;
  }
  
  return GenericSportsIcon;
};

/**
 * Get sport icon component by sport key
 * @param {string} sportKey - Sport key (e.g., 'americanfootball_nfl', 'basketball_nba')
 * @returns {React.Component} Icon component
 */
export const getSportIconByKey = (sportKey) => {
  if (!sportKey) return GenericSportsIcon;
  
  const IconComponent = SPORT_KEY_TO_ICON[sportKey];
  return IconComponent || GenericSportsIcon;
};

/**
 * React component that renders a sport icon
 * @param {Object} props - Component props
 * @param {string} props.sportTitle - Sport title
 * @param {string} props.sportKey - Sport key (alternative to sportTitle)
 * @param {Object} props.sx - Material-UI sx prop for styling
 * @param {string} props.fontSize - Icon size
 * @returns {React.Element} Sport icon element
 */
export const SportIcon = ({ sportTitle, sportKey, sx, fontSize, ...props }) => {
  let IconComponent;
  
  if (sportKey) {
    IconComponent = getSportIconByKey(sportKey);
  } else if (sportTitle) {
    IconComponent = getSportIconByTitle(sportTitle);
  } else {
    IconComponent = GenericSportsIcon;
  }
  
  return <IconComponent sx={sx} fontSize={fontSize} {...props} />;
};

export default SportIcon;